/**
 * Event card: date tab (overlapping top) + square image-led card with title overlay,
 * event type pill, CTA on image, and lower copy panel. Combines date-tab treatment
 * with promotional card style; twentytwentysix design language.
 * Click opens EventDetailModal (same interaction pattern as M List listings).
 */
import React, { useState } from "react";
import EventDetailModal from "./EventDetailModal";

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function getDateTab(ev) {
  const raw = ev.start_datetime || ev.start_date;
  if (!raw) return null;
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return { month: MONTHS[d.getMonth()], day: String(d.getDate()) };
  } catch (_) {
    return null;
  }
}

export default function EventCard({ event: ev }) {
  const [detailOpen, setDetailOpen] = useState(false);
  if (!ev) return null;
  const name = ev.name ?? "Event";
  const datetime = ev.pretty_datetime || ev.start_datetime;
  const hasImage = !!(ev.image || ev.graphic_400 || ev.photo);
  const imageUrl = ev.image || ev.graphic_400 || ev.photo;
  const dateTab = getDateTab(ev);
  const eventType = ev.event_type || ev.category || ev.event_type_name || null;
  const ctaUrl = ev.url || ev.registration_url || null;
  const ctaLabel = ev.cta_label || (ctaUrl ? "More info" : null);

  return (
    <>
      <li className="twentytwentysix-content__event-card">
        {dateTab && (
          <div className="twentytwentysix-content__event-date-tab" aria-label={`Date: ${dateTab.month} ${dateTab.day}`}>
            <span className="twentytwentysix-content__event-date-tab-month">{dateTab.month}</span>
            <span className="twentytwentysix-content__event-date-tab-day">{dateTab.day}</span>
          </div>
        )}
        <button
          type="button"
          className="twentytwentysix-content__event-card-inner"
          onClick={() => setDetailOpen(true)}
          aria-label={`View details for ${name}`}
        >
          <div className="twentytwentysix-content__event-image-wrap">
            {hasImage ? (
              <img src={imageUrl} alt={name} className="twentytwentysix-content__event-image" />
            ) : (
              <div className="twentytwentysix-content__event-image-placeholder" aria-hidden="true" />
            )}
            <div className="twentytwentysix-content__event-image-overlay">
              {eventType && (
                <span className="twentytwentysix-content__event-type-pill">{eventType}</span>
              )}
              <h3 className="twentytwentysix-content__event-card-title">{name}</h3>
              {ctaUrl && ctaLabel && (
                <a
                  href={ctaUrl.startsWith("http") ? ctaUrl : `https://${ctaUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="twentytwentysix-content__event-card-cta-btn"
                  onClick={(e) => e.stopPropagation()}
                >
                  {ctaLabel}
                </a>
              )}
            </div>
          </div>

          <div className="twentytwentysix-content__event-card-body">
            {ev.short_description ? (
              <p className="twentytwentysix-content__event-card-desc">{ev.short_description}</p>
            ) : (
              <div className="twentytwentysix-content__event-card-meta-block">
                {datetime && (
                  <span className="twentytwentysix-content__event-card-meta" aria-label="Date and time">
                    {datetime}
                  </span>
                )}
                {ev.location && (
                  <span className="twentytwentysix-content__event-card-meta" aria-label="Location">
                    {ev.location}
                  </span>
                )}
              </div>
            )}
            {ev.short_description && (datetime || ev.location) && (
              <div className="twentytwentysix-content__event-card-meta-block twentytwentysix-content__event-card-meta-block--secondary">
                {datetime && <span className="twentytwentysix-content__event-card-meta">{datetime}</span>}
                {ev.location && <span className="twentytwentysix-content__event-card-meta">{ev.location}</span>}
              </div>
            )}
            {ctaUrl && !ctaLabel && (
              <a
                href={ctaUrl.startsWith("http") ? ctaUrl : `https://${ctaUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                className="twentytwentysix-content__link twentytwentysix-content__event-card-cta"
                onClick={(e) => e.stopPropagation()}
              >
                Event details
              </a>
            )}
          </div>
        </button>
      </li>

      {detailOpen && (
        <EventDetailModal
          event={ev}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </>
  );
}
